import React, { useState, useEffect, useRef } from "react";
import {
  addressToGroupAndPersonelAdds,
  getApartmentsDetails,
  getDistricts,
  getNeighborhoods,
  getStreets,
} from "../services/addressService";
import { getAllGroups } from "../services/groupService";
import "../styles/pages/_address-assignments.scss";
import { getAllPersonelUsers } from "../services/userService";
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';

const AddressAssignments = ({ currentFilters = {} }) => {
  const toast = useRef(null);
  const [selectedFilters, setSelectedFilters] = useState(currentFilters);
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [streets, setStreets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState({
    districts: false,
    neighborhoods: false,
    streets: false,
  });

  // Updated state to handle new response structure
  const [streetsData, setStreetsData] = useState([]);
  const [loadingApartments, setLoadingApartments] = useState(false);
  const [apartmentGroups, setApartmentGroups] = useState({});

  // Yeni state: Grup seçenekleri
  const [groupOptions, setGroupOptions] = useState([]);

  // Modal state'leri
  const [showHaneModal, setShowHaneModal] = useState(false);
  const [selectedApartment, setSelectedApartment] = useState(null);

  // Collapsible state for streets
  const [collapsedStreets, setCollapsedStreets] = useState(new Set());

  // Street search functionality
  const [streetSearchTerm, setStreetSearchTerm] = useState("");

  // Batch selection functionality
  const [selectedApartments, setSelectedApartments] = useState(new Set());
  const [batchGroup, setBatchGroup] = useState("");
  const [masterSelections, setMasterSelections] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isBatchAssigning, setIsBatchAssigning] = useState(false);

  // Toggle street collapse function
  const toggleStreetCollapse = (streetIndex) => {
    setCollapsedStreets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(streetIndex)) {
        newSet.delete(streetIndex);
      } else {
        newSet.add(streetIndex);
      }
      return newSet;
    });
  };

  // Toggle apartment selection
  const toggleApartmentSelection = (apartmentId) => {
    setSelectedApartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(apartmentId)) {
        newSet.delete(apartmentId);
      } else {
        newSet.add(apartmentId);
      }
      return newSet;
    });
  };

  // Toggle master selection for a street
  const toggleMasterSelection = (streetIndex) => {
    const street = filteredStreetsData[streetIndex];
    const apartmentIds = street.apartmanlar.map(apt => apt.id);
    
    setMasterSelections(prev => {
      const newMasterSelections = { ...prev };
      if (newMasterSelections[streetIndex]) {
        // Deselect all apartments in this street
        setSelectedApartments(prev => {
          const newSet = new Set(prev);
          apartmentIds.forEach(id => newSet.delete(id));
          return newSet;
        });
        delete newMasterSelections[streetIndex];
      } else {
        // Select all apartments in this street
        setSelectedApartments(prev => {
          const newSet = new Set(prev);
          apartmentIds.forEach(id => newSet.add(id));
          return newSet;
        });
        newMasterSelections[streetIndex] = true;
      }
      return newMasterSelections;
    });
  };

  // Handle batch group assignment
  const handleBatchGroupAssignment = async () => {
    if (!batchGroup || selectedApartments.size === 0) {
      alert("Lütfen bir grup seçin ve en az bir apartman seçin!");
      return;
    }

    setIsBatchAssigning(true);
    try {
      // Collect unique sokak names for selected apartments
      const selectedApartmentIds = Array.from(selectedApartments);
      const sokakSet = new Set();
      streetsData.forEach(street => {
        street.apartmanlar.forEach(apartment => {
          if (selectedApartmentIds.includes(apartment.id)) {
            sokakSet.add(street.sokak);
          }
        });
      });
      const sokakAdlari = Array.from(sokakSet).join(',');
      const response = await addressToGroupAndPersonelAdds(selectedFilters.district.label, selectedFilters.neighborhood.label, sokakAdlari, selectedUsers, batchGroup);
      // If the response is a file, trigger download
      if (response && response.data) {
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Format date as DD.MM.YYYY
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const formattedDate = `${day}.${month}.${year}`;
        a.download = `${selectedFilters.district.label}-${selectedFilters.neighborhood.label}-${formattedDate}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
      }
      // Update local state to reflect the assignment
      setApartmentGroups(prev => {
        const newGroups = { ...prev };
        selectedApartments.forEach(apartmentId => {
          newGroups[apartmentId] = batchGroup;
        });
        return newGroups;
      });
      // Clear selections and input fields
      setSelectedApartments(new Set());
      setBatchGroup("");
      setSelectedUsers([]);
      setMasterSelections({});
      toast.current.show({
        severity: 'success',
        summary: 'Başarılı',
        detail: 'Grup ataması başarıyla tamamlandı!',
        life: 3000
      });
    } catch (error) {
      console.error("Toplu grup atama hatası:", error);
      alert("Grup atama işlemi başarısız oldu!");
    } finally {
      setIsBatchAssigning(false);
    }
  };

  // Hane modal'ını açma fonksiyonu
  const openHaneModal = (apartment) => {
    setSelectedApartment(apartment);
    setShowHaneModal(true);
  };

  // Hane modal'ını kapatma fonksiyonu
  const closeHaneModal = () => {
    setShowHaneModal(false);
    setSelectedApartment(null);
  };
  const fetchDays = async () => {
    try {
      const days = [
        "Pazartesi",
        "Salı",
        "Çarşamba",
        "Perşembe",
        "Cuma",
        "Cumartesi",
        "Pazar"
      ];
  
      const formatted = days.map(day => ({
        label: day,
        value: day
      }));
  
      setGroupOptions(formatted);
    } catch (error) {
      console.error("Günler alınırken hata:", error);
    }
  };
  
  // fetchGroups fonksiyonunu bileşen içinde tanımla
  const fetchGroups = async () => {
    try {
      // Örnek: Token'ı localStorage'dan veya başka bir yerden alıyoruz
      const response = await getAllGroups();

      if (Array.isArray(response)) {
        const formatted = response
          .filter((group) => !group.isDeleted)
          .map((g) => ({
            label: g.name,
            value: g.id,
          }));
        setGroupOptions(formatted);
      } else {
        console.warn("Grup verisi beklenen formatta değil:", response);
      }
    } catch (error) {
      console.error("Grup verisi çekilirken hata:", error);
    }
  };

  // İlk renderda ilçeleri, grupları ve kullanıcıları çek
  useEffect(() => {
    fetchDistricts();
    fetchDays();

    fetchUsers();
  }, []);

  // İlçeleri çek
  const fetchDistricts = async () => {
    try {
      setLoading((prev) => ({ ...prev, districts: true }));
      const response = await getDistricts();
      if (response.status === 200) {
        setDistricts(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch districts:", error);
    } finally {
      setLoading((prev) => ({ ...prev, districts: false }));
    }
  };

  // Kullanıcıları çek

  const fetchUsers = async () => {
    try {
      const response = await getAllPersonelUsers();
      setUsers(response);
    }
    catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  useEffect(() => {
    console.log('apartmentGroups: ', apartmentGroups);
  }, [apartmentGroups]);


  // Mahalleleri çek
  useEffect(() => {
    if (selectedFilters.district?.value) {
      fetchNeighborhoods(selectedFilters.district.value);
    } else {
      setNeighborhoods([]);
      setStreets([]);
      setSelectedFilters((prev) => ({
        ...prev,
        neighborhood: null,
        street: null,
      }));
    }
  }, [selectedFilters.district]);

  const fetchNeighborhoods = async (districtId) => {
    try {
      setLoading((prev) => ({ ...prev, neighborhoods: true }));
      const response = await getNeighborhoods(districtId);
      if (response.status === 200) {
        setNeighborhoods(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch neighborhoods:", error);
    } finally {
      setLoading((prev) => ({ ...prev, neighborhoods: false }));
    }
  };

  // Sokakları çek
  useEffect(() => {
    if (selectedFilters.neighborhood?.value) {
      fetchStreets(selectedFilters.neighborhood.value);
    } else {
      setStreets([]);
      setSelectedFilters((prev) => ({ ...prev, street: null }));
    }
  }, [selectedFilters.neighborhood]);

  const fetchStreets = async (neighborhoodId) => {
    try {
      setLoading((prev) => ({ ...prev, streets: true }));
      const response = await getStreets(neighborhoodId);
      if (response.status === 200) {
        setStreets(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch streets:", error);
    } finally {
      setLoading((prev) => ({ ...prev, streets: false }));
    }
  };

  // Filtre değişimi için yardımcı fonksiyon
  const handleInputChange = (key, value) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      const newFilters = { ...selectedFilters };
      delete newFilters[key];
      setSelectedFilters(newFilters);
    } else {
      setSelectedFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
    
    // Clear street search and reset collapsed states when filters change
    setStreetSearchTerm("");
    setCollapsedStreets(new Set());
    setSelectedApartments(new Set());
    setBatchGroup("");
    setMasterSelections({});
  };

  // Filtrele butonuna basıldığında apartmanları çek - Updated for new structure
  const handleFilterClick = async () => {
    if (!selectedFilters.district || !selectedFilters.neighborhood) {
      alert("Lütfen önce ilçe ve mahalle seçin!");
      return;
    }

    setLoadingApartments(true);
    try {
      const neighborhood = encodeURIComponent(
        selectedFilters.neighborhood.label
      );
      const district = encodeURIComponent(selectedFilters.district.label);
      const response = await getApartmentsDetails(neighborhood, district);

      try {
        const json = response.data;

        // Handle the new response structure
        const streetsList = Array.isArray(json)
          ? json
          : json
          ? [json]
          : [];

        setStreetsData(streetsList);
        
        // Clear street search and reset all collapsed states when new data is loaded
        setStreetSearchTerm("");
        setCollapsedStreets(new Set());
        setSelectedApartments(new Set());
        setBatchGroup("");
        setMasterSelections({});
      } catch (jsonError) {
        console.error("❌ JSON parse edilemedi!", jsonError);
        setStreetsData([]);
      }
    } catch (error) {
      console.error("❗ fetch çağrısında hata:", error);
      setStreetsData([]);
    } finally {
      setLoadingApartments(false);
    }
  };

  useEffect(() => {
    console.log('streetsData: ', streetsData);
  }, [streetsData]);

  // Collapse all streets whenever streetsData changes
  useEffect(() => {
    if (streetsData.length > 0) {
      setCollapsedStreets(new Set(streetsData.map((_, idx) => idx)));
    } else {
      setCollapsedStreets(new Set());
    }
  }, [streetsData]);

  // Calculate total hane count in all streets
  const totalHaneCount = streetsData.reduce((sum, street) => sum + street.apartmanlar.reduce((aptSum, apt) => aptSum + (apt.haneler ? apt.haneler.length : 0), 0), 0);

  // Filter streets based on search term
  const filteredStreetsData = streetsData.filter(street => 
    street.sokak.toLowerCase().includes(streetSearchTerm.toLowerCase())
  );

  // Calculate the total number of hanes for selected apartments
  const selectedHaneCount = streetsData.reduce((total, street) => {
    return total + street.apartmanlar.reduce((aptSum, apartment) => {
      if (selectedApartments.has(apartment.id)) {
        return aptSum + (apartment.haneler ? apartment.haneler.length : 0);
      }
      return aptSum;
    }, 0);
  }, 0);

  // Ref for batch assignment container
  const batchAssignmentRef = useRef(null);
  const [isBatchAbsolute, setIsBatchAbsolute] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setIsBatchAbsolute(true);
      } else {
        setIsBatchAbsolute(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="people-container neighborhood-container">
      <div className="filter-form" style={{padding: "0"}}>
        <div className="form-row">
          <div className="form-group">
            <label>İlçe</label>
            <select
              value={selectedFilters.district?.value || ""}
              onChange={(e) => {
                const selectedDistrict = districts.find(
                  (d) => d.id === e.target.value
                );
                handleInputChange(
                  "district",
                  selectedDistrict
                    ? {
                        value: selectedDistrict.id,
                        label: selectedDistrict.ad,
                      }
                    : null
                );
              }}
              className="filter-input"
              disabled={loading.districts}
            >
              <option value="">İlçe Seçiniz</option>
              {districts.map((district) => (
                <option key={district.id} value={district.id}>
                  {district.ad}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Mahalle</label>
            <select
              value={selectedFilters.neighborhood?.value || ""}
              onChange={(e) => {
                const selectedNeighborhood = neighborhoods.find(
                  (n) => n.id === e.target.value
                );
                handleInputChange(
                  "neighborhood",
                  selectedNeighborhood
                    ? {
                        value: selectedNeighborhood.id,
                        label: selectedNeighborhood.ad,
                      }
                    : null
                );
              }}
              className="filter-input"
              disabled={loading.neighborhoods || !selectedFilters.district}
            >
              <option value="">Mahalle Seçiniz</option>
              {neighborhoods.map((neighborhood) => (
                <option key={neighborhood.id} value={neighborhood.id}>
                  {neighborhood.ad}
                </option>
              ))}
            </select>
          </div>

          <div
            className="form-group address-assignments-filter-button-container"
            style={{ minWidth: "100px", alignSelf: "flex-end" }}
          >
            <button
              type="button"
              className="neighborhood-filter-submit-button"
              onClick={handleFilterClick}
              style={{ width: "100%" }}
              disabled={loadingApartments}
            >
              {loadingApartments ? "Yükleniyor..." : "Filtrele"}
            </button>
            <a className="neighborhood-gorev-planlari-link" href="/gorev-planlari">Görev Planları</a>
          </div>
        </div>
      </div>

      {/* Street Search */}
      {streetsData.length > 0 && (
        <div className="street-search-container">
          <div className="street-search-row">
            <div className="street-search-group">
              <label>Sokak Ara:</label>
              <input
                type="text"
                placeholder="Sokak adını yazın..."
                value={streetSearchTerm}
                onChange={(e) => setStreetSearchTerm(e.target.value)}
                className="street-search-input"
              />
            </div>
            {streetSearchTerm && (
              <button
                type="button"
                onClick={() => setStreetSearchTerm("")}
                className="clear-search-button"
              >
                Temizle
              </button>
            )}
          </div>
        </div>
      )}

      {/* Batch Group Assignment */}
      {streetsData.length > 0 && (
        <div ref={batchAssignmentRef} className={`batch-assignment-container${isBatchAbsolute ? " batch-absolute" : ""}`}>
          <div className="batch-assignment-row">
            <div className="batch-assignment-group hidden">
              <label>Görev atama işlemi</label>
              <select
                value={batchGroup}
                onChange={(e) => setBatchGroup(e.target.value)}
                className="batch-group-select"
              >
                <option value="">Gün Seçiniz</option>
                {groupOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              </select>
            </div>
            <div className="batch-assignment-group">
              <label>Kullanıcı Seçiniz:</label>
              <MultiSelect
                value={selectedUsers}
                options={users.map(user => ({ label: user.adSoyad, value: user.id }))}
                onChange={e => setSelectedUsers(e.value)}
                optionLabel="label"
                placeholder="Kullanıcı Seçiniz"
                className="filter-input batch-group-select"
                filter
                display="chip"
              />
            </div>
            <button
              type="button"
              onClick={handleBatchGroupAssignment}
              className="batch-assign-button"
              disabled={!batchGroup || selectedApartments.size === 0 || isBatchAssigning}
              dangerouslySetInnerHTML={{ __html: isBatchAssigning ? `<span class='spinner' style='display:inline-block;width:18px;height:18px;border:2px solid #fff;border-top:2px solid #18964e;border-radius:50%;animation:spin 1s linear infinite;vertical-align:middle;margin-right:8px;'></span> Atanıyor...` : `Seçilenleri Ata <br/>(${selectedApartments.size} Apartman, ${selectedHaneCount} Hane)` }}
            />
          </div>
        </div>
      )}

      {/* Streets and Apartments Tables */}
      {filteredStreetsData.length > 0 && (
                <div className="streets-container">
          {filteredStreetsData.map((street, filteredIndex) => {
            // Find the original index in streetsData for collapse functionality
            const originalIndex = streetsData.findIndex(s => s.sokak === street.sokak);
            return (
              <div key={originalIndex} className="street-section">
                <h3 className="street-title">
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={masterSelections[filteredIndex] || false}
                      onChange={(e) => toggleMasterSelection(filteredIndex)}
                      className="master-checkbox"
                    />
                  </div>
                  <div className="street-center" onClick={() => toggleStreetCollapse(originalIndex)}>
                    <span className="collapse-icon">{collapsedStreets.has(originalIndex) ? "▶" : "▼"}</span>
                    Sokak: {street.sokak} (Toplam Hane: {street.apartmanlar.reduce((sum, apt) => sum + (apt.haneler ? apt.haneler.length : 0), 0)}, Aktif Hane: {street.apartmanlar.reduce((sum, apt) => sum + (apt.haneler ? apt.haneler.length : 0), 0) - street.apartmanlar.reduce((sum, apt) => sum + (apt.bloklananlar || 0), 0)}, Pasif Hane: {street.apartmanlar.reduce((sum, apt) => sum + (apt.bloklananlar || 0), 0)})
                  </div>
                </h3>
                {!collapsedStreets.has(originalIndex) && (
                <table className="apartments-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={masterSelections[filteredIndex] || false}
                          onChange={() => toggleMasterSelection(filteredIndex)}
                          className="master-checkbox"
                        />
                      </th>
                      <th>Apartman Adı</th>
                      <th>Hane Sayısı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {street.apartmanlar.map((apartment) => {
                      const isGroupSelected = apartmentGroups[apartment.id] || apartment.grup || "";
                      const isSelected = selectedApartments.has(apartment.id);

                      return (
                        <tr key={apartment.id} className={isSelected ? "selected-row" : ""}>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleApartmentSelection(apartment.id)}
                              className="apartment-checkbox"
                            />
                          </td>
                          <td>{apartment.apartmanAdi}</td>
                          <td 
                            className="hane-count-cell"
                            onClick={() => openHaneModal(apartment)}
                          >
                            {apartment.haneler.length}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
             )}
           </div>
         );
       })}
       </div>
     )}

      {streetsData.length === 0 && !loadingApartments && (
        <p style={{ marginTop: "20px", fontStyle: "italic", color: "#666" }}>
          Seçilen mahallede henüz kayıtlı sokak bulunamadı.
        </p>
      )}

      {/* Grup Özeti Tablosu - Fixed Position */}
      {streetsData.length > 0 && (
        <div className="group-summary-container">
          <table className="group-summary-table">
            <thead>
              <tr>
                <th>Toplam Hane Sayısı</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{totalHaneCount}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Hane Detay Modal */}
      {showHaneModal && selectedApartment && (
        <div className="hane-modal-overlay" onClick={closeHaneModal}>
          <div className="hane-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="hane-modal-header">
              <h3>{selectedApartment.apartmanAdi} Apartmanı - Haneler</h3>
              <button className="hane-modal-close" onClick={closeHaneModal}>
                ×
              </button>
            </div>
            <div className="hane-modal-body">
              {selectedApartment.haneler && selectedApartment.haneler.length > 0 ? (
                <div className="hane-list">
                  {selectedApartment.haneler.map((hane, index) => (
                    <div key={index} className="hane-item">
                      {hane}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-hane-message">Bu apartmanda henüz hane kaydı bulunmamaktadır.</p>
              )}
            </div>
          </div>
        </div>
      )}
      <Toast ref={toast} />
    </div>
  );
};

export default AddressAssignments;
